// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/components/ui/DeleteConfirmationOffcanvas.jsx
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect } from "react";
import { X, AlertCircle, ArrowRight } from "lucide-react";

const DeleteConfirmationOffcanvas = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  itemName,
  error,
  onNavigateToUsers,
  onViewUser,
  children,
  isLoading,
  deleteType = "hard",
  navigateButtonText = "Go to Users Tab", 
  confirmButtonText = "Delete",        // ✅ Added: Default fallback to keep existing deletes safe
  customDescription = null,            // ✅ Added: Default fallback to keep existing descriptions safe
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed top-[57px] md:top-[65px] inset-0 z-50 bg-black bg-opacity-30 transition-opacity duration-300 ${
        isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
      onClick={onClose}
    >
      <div
        className={`absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl mx-auto bg-white shadow-xl rounded-b-2xl transform transition-transform duration-300 pb-6 ${
          isOpen ? 'translate-y-0' : '-translate-y-full'
        } overflow-y-auto max-h-[90vh]`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="py-6 px-2 md:px-6">
          {/* Header */}
          <div className="flex items-start justify-between">
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
            <button
              type="button"
              className="flex h-10 w-10 items-center justify-center rounded-full text-gray-400 hover:text-gray-600"
              onClick={onClose}
            >
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          {/* Content */}
          <div className="">
            {!error ? (
              <div className="p-2 md:p-4">
                <p className="text-gray-600 text-base">
                  {/* ✅ If customDescription is passed, render it directly. Else, fall back to default delete text */}
                  {customDescription ? (
                    customDescription
                  ) : (
                    <>
                      Are you sure you want to delete{" "}
                      <span className="font-semibold text-gray-800">{itemName}</span>?
                      {deleteType === "soft" ? (
                        <>
                          {" "}This client will be{" "}
                          <span className="text-red-600 font-semibold">marked as Deleted</span>
                          {" "}and its status will be updated to{" "}
                          <span className="text-red-600 font-semibold">Deleted</span>.
                        </>
                      ) : (
                        <>
                          {" "}This action is{" "}
                          <span className="text-red-600 font-semibold">permanent</span> and{" "}
                          <span className="text-red-600 font-semibold">
                            cannot be recovered
                          </span>.
                        </>
                      )}
                    </>
                  )}
                </p>
                <div className="mt-4">
                   {children} 
                </div>
                {/* Buttons Centered */}
                <div className="mt-8 flex justify-center gap-4">
                  <button
                    type="button"
                    className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={onClose}
                    disabled={isLoading} 
                  >
                    Cancel
                  </button>
                  
                  <button
                    type="button"
                    className={`px-5 py-2 rounded-lg text-sm font-medium text-white shadow transition-all ${
                      isLoading ? "bg-red-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"
                    }`}
                    onClick={onConfirm}
                    disabled={isLoading} 
                  >
                    {/* ✅ Uses the customized or defaulted confirmation text */}
                    {isLoading ? `${confirmButtonText}ing...` : confirmButtonText} 
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 border-l-4 border-red-500 p-2 md:p-4 rounded-lg">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-700">{error.message}</p>

                    <ul className="mt-2 list-disc md:pl-5 space-y-1 text-sm text-red-700">
                      {error.users.map((user) => (
                        <li key={user._id} className="flex items-center justify-between">
                          <span>
                            {user.name} ({user.email})
                          </span>
                          <button
                            onClick={() => onViewUser(user.id)}
                            className="text-red-600 hover:text-red-800 flex items-center text-sm ml-2"
                          >
                            View <ArrowRight className="h-4 w-4 ml-1" />
                          </button>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-4 flex justify-center gap-4">
                      <button
                        type="button"
                        onClick={onNavigateToUsers}
                        className="bg-red-100 text-red-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-red-200"
                      >
                        {navigateButtonText}
                      </button>
                      <button
                        type="button"
                        onClick={onClose}
                        className="bg-white text-gray-500 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-50"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationOffcanvas;